import type {JOB_INFORMATION} from 'src/types/crm';
import type {TranslationFunction} from 'src/types/language-support';
import type {TaskJobDetails} from 'src/types/task-and-reminder';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';

import * as React from 'react';
import debounce from 'lodash/debounce';
import {useDispatch, useSelector} from 'react-redux';

import {SimpleTypeahead} from '@spaced-out/ui-design-system/lib/components/Typeahead';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {selectJobInformation} from 'src/selectors/crm';
import * as reduxApi from 'src/utils/redux-api-v2';

import {mergeTypeaheadSelectedOption} from 'src/components/task-and-reminder/lib/merge-typeahead-selected-option';


const DEFAULT_JOB_OPTIONS_PAGE_SIZE = 10;

type FilteredJobHiringTeamMember = {
  jobUserRole?: string | null;
  user?: {
    fullName?: string | null;
    prettyName?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
};

type FilteredJobRow = {
  id: string | number;
  title?: string | null;
  code?: string | null;
  recruiter?: FilteredJobHiringTeamMember | null;
  hiringManager?: FilteredJobHiringTeamMember | null;
};

type FilteredJobsResponse = {
  content?: Array<FilteredJobRow>;
};

const RECRUITER_ROLE = 'RECRUITER';
const HIRING_MANAGER_ROLE = 'HIRING_MANAGER';

const getJobHiringTeamMembers = (
  job: JOB_INFORMATION,
): Array<FilteredJobHiringTeamMember> => {
  const members = (job as {hiringTeamMembers?: unknown}).hiringTeamMembers;
  return Array.isArray(members)
    ? (members as Array<FilteredJobHiringTeamMember>)
    : [];
};

const getHiringTeamMemberName = (
  member: FilteredJobHiringTeamMember | null | undefined,
): string => {
  const user = member?.user;
  if (!user) {
    return '';
  }
  const fullName = (
    user.fullName ||
    user.prettyName ||
    `${user.firstName ?? ''} ${user.lastName ?? ''}`
  ).trim();
  return fullName;
};

const buildJobSecondaryLabel = (
  job: FilteredJobRow,
  t: TranslationFunction,
): string => {
  const jobMetadata = [
    {
      label: t('Recruiter', 'Recruiter'),
      value: getHiringTeamMemberName(job.recruiter),
    },
    {
      label: t('Hiring_Manager', 'Hiring Manager'),
      value: getHiringTeamMemberName(job.hiringManager),
    },
  ];

  return jobMetadata
    .filter(
      (segment) =>
        segment.value !== null &&
        segment.value !== undefined &&
        String(segment.value).trim() !== '',
    )
    .map((segment) => `${segment.label}: ${String(segment.value).trim()}`)
    .join(' | ');
};

type TaskJobSelectFieldProps = {
  label: string;
  placeholder: string;
  value: TaskJobDetails | null;
  onChange: (next: TaskJobDetails | null) => void;
  className?: string;
};

export const TaskJobSelectField = ({
  label,
  placeholder,
  value,
  onChange,
  className,
}: TaskJobSelectFieldProps) => {
  const dispatch = useDispatch();
  const {t} = useLabelTranslation();
  const jobInfo = useSelector(selectJobInformation);
  const [filteredList, setFilteredList] = React.useState<Array<FilteredJobRow>>(
    [],
  );
  const [isLoading, setIsLoading] = React.useState(false);

  // Reuse the job already loaded in the store (e.g. the job info page the task
  // was created from) to show the pre-selected option's recruiter/hiring
  // manager, instead of re-fetching it.
  const selectedJob = React.useMemo<FilteredJobRow | null>(() => {
    const id = value?.id ? String(value.id) : '';
    if (!id || !jobInfo || String(jobInfo.id) !== id) {
      return null;
    }
    const members = getJobHiringTeamMembers(jobInfo);
    return {
      id,
      title: jobInfo.title ?? value?.title ?? '',
      code: jobInfo.code ?? null,
      recruiter:
        members.find((member) => member.jobUserRole === RECRUITER_ROLE) ?? null,
      hiringManager:
        members.find((member) => member.jobUserRole === HIRING_MANAGER_ROLE) ??
        null,
    };
  }, [jobInfo, value?.id, value?.title]);

  const typeaheadOptions = React.useMemo(
    () =>
      mergeTypeaheadSelectedOption(
        filteredList.map((job) => ({
          key: String(job.id),
          label: job.title || '',
          secondaryLabel: buildJobSecondaryLabel(job, t),
        })),
        value?.id
          ? {
              id: String(value.id),
              label: value.title ?? '',
              secondaryLabel: selectedJob
                ? buildJobSecondaryLabel(selectedJob, t)
                : undefined,
            }
          : null,
      ),
    [filteredList, value?.id, value?.title, selectedJob, t],
  );

  const selectedKeys = React.useMemo(
    () => (value?.id ? [String(value.id)] : []),
    [value?.id],
  );

  const resolveJobLabel = React.useCallback(
    (option: MenuOption): React.ReactNode => {
      const job =
        filteredList.find((row) => String(row.id) === option.key) ??
        (String(selectedJob?.id) === option.key ? selectedJob : undefined);
      const code = (job?.code ?? '').trim() || option.key;
      return (
        <div>
          {option.label}
          {code ? <span>{` | ${code}`}</span> : null}
        </div>
      );
    },
    [filteredList, selectedJob],
  );

  const fetchFilteredList = React.useCallback(
    async (query: string): Promise<Array<FilteredJobRow>> => {
      const response = (await dispatch(
        reduxApi.post(
          `/api/filtered-jobs`,
          {
            jobStatuses: ['OPEN'],
            titleOrCode: query,
            template: false,
            referralActive: false,
            publicAccess: false,
            page: 0,
            pageSize: DEFAULT_JOB_OPTIONS_PAGE_SIZE,
          },
          {},
          {apiPath: '/trmapi'},
        ),
      )) as FilteredJobsResponse | null | undefined;
      const content = response?.content;
      const rows = Array.isArray(content) ? content : [];
      return rows.slice(0, DEFAULT_JOB_OPTIONS_PAGE_SIZE);
    },
    [dispatch],
  );

  const debouncedSearch = React.useMemo(
    () =>
      debounce((query: string) => {
        void (async () => {
          setIsLoading(true);
          try {
            const list = await fetchFilteredList(query);
            setFilteredList(list);
          } finally {
            setIsLoading(false);
          }
        })();
      }, 400),
    [fetchFilteredList],
  );

  React.useEffect(() => {
    void (async () => {
      setIsLoading(true);
      try {
        const list = await fetchFilteredList('');
        setFilteredList(list);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [fetchFilteredList]);

  const handleSelect = (option: MenuOption) => {
    const row = filteredList.find((job) => String(job.id) === option.key);
    const title = row?.title || option.label || '';
    onChange({id: option.key, title});
  };

  const handleClear = () => {
    onChange(null);
    void (async () => {
      setIsLoading(true);
      try {
        const list = await fetchFilteredList('');
        setFilteredList(list);
      } finally {
        setIsLoading(false);
      }
    })();
  };

  return (
    <div className={className}>
      <SimpleTypeahead
        label={label}
        placeholder={placeholder}
        selectedKeys={selectedKeys}
        menuOpenOffset={0}
        onSearch={(evt) => {
          debouncedSearch(evt.target.value);
        }}
        onSelect={handleSelect}
        onClear={handleClear}
        options={typeaheadOptions}
        resolveLabel={resolveJobLabel}
        isLoading={isLoading}
        testId="task-job-select"
      />
    </div>
  );
};
